import { memo, useEffect } from "react";
import { useAtom, useAtomValue } from 'jotai';
import { Spin } from 'antd';
import { s2singleItemsLEFTAtom, s2singleItemsRIGHTAtom, s2singleLoadingAtom } from "./store";
import { API_URL_ADMIN } from "~/config/config";

const S2SingleMapController = () => {
  const [s2singleItemsLeft, setS2SingleItemsLeft] = useAtom(s2singleItemsLEFTAtom);
  const [s2singleItemsRight, setS2SingleItemsRight] = useAtom(s2singleItemsRIGHTAtom);
  const s2singleLoading = useAtomValue(s2singleLoadingAtom);

  const setupTransformRequest = (map: any) => {
    if (!map) return;
    const jwt = localStorage.getItem('jwt');
    if (!jwt) return;

    map.setTransformRequest((url: string, resourceType: any) => {
      if (resourceType === 'Tile' && url.includes('/remote-image-alert-ext/')) {
        return {
          url,
          headers: { Authorization: `Bearer ${jwt}` },
        };
      }
      return { url };
    });
  };

  const syncLayers = async (
    map: any,
    items: any[],
    prefix: string,
    setItems: (updater: (prev: any[]) => any[]) => void,
  ) => {
    if (!map) return;

    setupTransformRequest(map);

    // 1. Remove layers not in list
    const style = map.getStyle();
    if (style && style.layers) {
      style.layers.forEach((l: any) => {
        if (l.id.startsWith(prefix)) {
          const id = l.id.replace(prefix, '');
          if (!items.find((i: any) => i.id === id)) {
            if (map.getLayer(l.id)) {
              map.removeLayer(l.id);
            }
            if (map.getSource(l.id)) {
              map.removeSource(l.id);
            }
          }
        }
      });
    }

    // 2. Add layers in list
    for (const item of items) {
      const layerId = `${prefix}${item.id}`;
      if (!map.getLayer(layerId)) {
        let wmsUrl = item.wmsUrl;

        // Route through Strapi tile proxy if missing or direct GEE URL
        if (!wmsUrl || wmsUrl.includes('earthengine.googleapis.com')) {
          wmsUrl = `${API_URL_ADMIN}/api/remote-image-alert-ext/sentinel-2/tile/{z}/{x}/{y}?image_id=${encodeURIComponent(item.id)}`;
        } else if (wmsUrl.startsWith('/')) {
          wmsUrl = `${API_URL_ADMIN}${wmsUrl}`;
        }

        const beforeId = map.getLayer('River labels') ? 'River labels' : undefined;

        try {
          map.addLayer(
            {
              id: layerId,
              type: 'raster',
              source: {
                type: 'raster',
                tiles: [wmsUrl],
                tileSize: 256,
              },
              paint: {
                'raster-opacity': 1,
              },
            },
            beforeId
          );
        } catch (err) {
          console.error('[S2SingleMapController] Error adding layer', layerId, err);
        }
      }
    }
  };

  useEffect(() => {
    syncLayers(window.beforeMap, s2singleItemsLeft, 's2single-layer-left-', setS2SingleItemsLeft);
  }, [s2singleItemsLeft]);

  useEffect(() => {
    syncLayers(window.map, s2singleItemsRight, 's2single-layer-right-', setS2SingleItemsRight);
  }, [s2singleItemsRight]);

  return (
    <>
      {s2singleLoading.isLoading && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 99999,
            backgroundColor: 'rgba(0, 0, 0, 0.82)',
            color: '#ffffff',
            padding: '20px 32px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
            backdropFilter: 'blur(6px)',
            pointerEvents: 'all',
          }}
        >
          <Spin size="large" />
          <span style={{ fontSize: '15px', fontWeight: 500, letterSpacing: '0.3px' }}>
            {s2singleLoading.text || 'Đang làm mới và chuẩn bị ảnh vệ tinh Sentinel-2...'}
          </span>
        </div>
      )}
    </>
  );
};

export default memo(S2SingleMapController);
